import React, { PropsWithChildren } from 'react';
import { makeStyles } from '@material-ui/core';
import AppsIcon from '@material-ui/icons/Apps';
import LibraryBooksIcon from '@material-ui/icons/LibraryBooks';
import PeopleIcon from '@material-ui/icons/People';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import SubjectIcon from '@material-ui/icons/Subject';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import DeveloperBoardIcon from '@material-ui/icons/DeveloperBoard';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import { useApi, configApiRef } from '@backstage/core-plugin-api';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => {
  const configApi = useApi(configApiRef);
  const links = configApi.getOptionalConfig("app.links")
  const developmentEnabled = configApi.getOptionalBoolean("app.development.enabled") === true
  return (
    <SidebarPage>
      <Sidebar>
        <SidebarLogo />
        <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
          <SidebarSearchModal />
        </SidebarGroup>
        <SidebarDivider />
        <SidebarGroup label="Menu" icon={<MenuIcon />}>
          {/* Global nav, not org-specific */}
          <SidebarItem icon={AppsIcon} to="catalog" text="Catalog" />
          <SidebarItem icon={LibraryBooksIcon} to="docs" text="TechDocs" />
          <SidebarItem icon={CreateComponentIcon} to="create" text="Create..." />
          {developmentEnabled && (
            <SidebarItem icon={DeveloperBoardIcon} to="development" text="Development" />
          )}
          {/* End global nav */}
          <SidebarDivider />
          <SidebarScrollWrapper>
            {/* Items in this group will be scrollable if they run out of space */}
          </SidebarScrollWrapper>
        </SidebarGroup>
        <SidebarSpace />
        {links && (
          <>
          <SidebarDivider />
          {links && links.has("authServer") && (
            <SidebarItem icon={PeopleIcon} to={links.getString("authServer")} text="Auth Server" target="_blank" />
          )}
          {links && links.has("deployments") && (
            <SidebarItem icon={CloudUploadIcon} to={links.getString("deployments")} text="Deployments" target="_blank" />
          )}
          {links && links.has("pipelines") && (
            <SidebarItem icon={SubjectIcon} to={links.getString("pipelines")} text="Pipelines" target="_blank" />
          )}
          </>
        )}
        <SidebarDivider />
        <SidebarGroup
          label="Settings"
          icon={<UserSettingsSignInAvatar />}
          to="/settings"
        >
          <SidebarSettings />
        </SidebarGroup>
      </Sidebar>
      {children}
    </SidebarPage>
  )
};
